import * as React from 'react';
import axios from 'axios';

import {FormField} from '../../data/reviewForm';
import {QuestionEntry, ScanEntry} from '../../data/Data';

const EMAIL_RE: RegExp = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

type FormProps = {
    children: React.ReactNode;
    questions: FormField[];
    personal: FormField[];
    subject: string;
    scans?: ScanEntry[];
};

type FormData = {
    questions: FieldCollection;
    personal: FieldCollection;
    accepted: boolean;
    send: boolean;
    valid: boolean;
};

export type FieldCollection = {
    [name: string]: FieldData;
};

export type FieldData = FormField & {
    value: string | string[];
    error: boolean;
};

export type ManageableForm = {
    data: FormData;
    dispatch: React.Dispatch<FormAction>;
};

export type FormGroup = 'questions' | 'personal';

export type FormAction = SetAction | SendAction | SendResultAction;

export type SetAction = {
    type: 'SET';
    payload: {
        group: FormGroup;
        name: string;
        value: string | string[];
    };
};

export type SendResultAction = {
    type: 'SEND_RESULT';
    payload: {};
};

export type SendAction = {
    type: 'SEND';
    payload: {};
};

export const FormContext: React.Context<ManageableForm> = React.createContext<ManageableForm>({
    data: {
        questions: {},
        personal: {},
        accepted: false,
        send: false,
        valid: false,
    },
    dispatch: (action: FormAction): void => undefined,
});

function isValidRadioField(field: FieldData): boolean {
    return field.value.length > 0;
}

function isValidCheckboxField(field: FieldData): boolean {
    return field.value.length > 0;
}

function isValidTextField(field: FieldData): boolean {
    return field.value.length > 2;
}

function isValidEmailField(field: FieldData): boolean {
    return EMAIL_RE.test(field.value as string);
}

function isValidTelField(field: FieldData): boolean {
    return field.value.length >= 10;
}

function isValidField(field: FieldData): boolean {
    switch (field.type) {
        case 'checkbox':
            return isValidCheckboxField(field);
        case 'radio':
            return isValidRadioField(field);
        case 'email':
            return isValidEmailField(field);
        case 'tel':
            return isValidTelField(field);
        default:
            return isValidTextField(field);
    }
}

function validateCollection(
    collection: FieldCollection,
): [boolean, FieldCollection] {
    let isValid: boolean = true;

    const newCollection: FieldCollection = Object.keys(collection).reduce(
        (acc: FieldCollection, name: string): FieldCollection => {
            const field: FieldData = collection[name];
            const isFieldValid: boolean = isValidField(field);

            isValid = isValid && isFieldValid;

            return {
                ...acc,
                [name]: {
                    ...field,
                    error: !isFieldValid,
                },
            };
        },
        {},
    );

    return [isValid, newCollection];
}

function reducer(data: FormData, action: FormAction): FormData {
    switch (action.type) {
        case 'SEND_RESULT':
            return {
                ...data,
                send: true,
            };
        case 'SET':
            return {
                ...data,
                [action.payload.group]: {
                    ...data[action.payload.group],
                    [action.payload.name]: {
                        ...data[action.payload.group][action.payload.name],
                        value: action.payload.value,
                    },
                },
            };
        case 'SEND':
            const [isQuestionsValid, questions] = validateCollection(
                data.questions,
            );
            const [isPersonalValid, personal] = validateCollection(
                data.personal,
            );

            return {
                ...data,
                questions,
                personal,
                valid: isQuestionsValid && isPersonalValid,
            };
        default:
            return data;
    }
}

function initialState(questions: FormField[], personal: FormField[]): FormData {
    return {
        valid: false,
        accepted: false,
        send: false,
        questions: questions.reduce(
            (
                acc: { [name: string]: FieldData },
                field: FormField,
            ): { [name: string]: FieldData } => {
                return {
                    ...acc,
                    [field.name]: {
                        ...field,
                        value: '',
                        error: false,
                    },
                };
            },
            {},
        ),
        personal: personal.reduce(
            (
                acc: { [name: string]: FieldData },
                field: FormField,
            ): { [name: string]: FieldData } => {
                return {
                    ...acc,
                    [field.name]: {
                        ...field,
                        value: '',
                        error: false,
                    },
                };
            },
            {},
        ),
    };
}

type PostData = {
    label: string;
    value: string;
};

function getCheckboxPostValue(field: FieldData): string {
    return (field.value as string[]).reduce(
        (acc: string, value: string, index: number): string => {
            const infix: string = index > 0 ? ' | ' : '';

            return `${acc}${infix}${
                (field.choices as string[])[Number(value)]
            }`;
        },
        '',
    );
}

function getRadioPostValue(field: FieldData): string {
    return (field.choices as string[])[Number(field.value)];
}

function getFieldPostValue(field: FieldData): string {
    switch (field.type) {
        case 'checkbox':
            return getCheckboxPostValue(field);
        case 'radio':
            return getRadioPostValue(field);
        default:
            return field.value as string;
    }
}

function getFormData(collection: FieldCollection): PostData[] {
    return Object.keys(collection).reduce(
        (acc: PostData[], name: string): PostData[] => {
            const field: FieldData = collection[name];

            return acc.concat({
                label: field.title === '' ? field.name : field.title,
                value: getFieldPostValue(field),
            });
        },
        [],
    );
}

function getScanData(scans: ScanEntry[]): PostData[] {
    return scans.reduce((acc: PostData[], scan: ScanEntry): PostData[] => {
        return acc.concat(
            Object.keys(scan.questions).map(
                (key: string): PostData => {
                    const entry: QuestionEntry = scan.questions[key];

                    return {
                        label: entry.title,
                        value: entry.answers[entry.answer as number],
                    };
                },
            ),
        );
    }, []);
}

// tslint:disable-next-line function-name
export function FormProvider(props: FormProps): JSX.Element {
    const [data, dispatch] = React.useReducer(
        reducer,
        initialState(props.questions, props.personal),
    );

    React.useEffect((): void => {
        const post: PostData[] = [];

        if (data.valid) {
            post.push(
                { label : 'Formulier',  value : props.subject },
                ...getFormData(data.questions),
                ...getFormData(data.personal),
            );

           if (props.scans !== undefined) {
               post.push(
                   { label : 'Scandata',  value : '' },
                   ...getScanData(props.scans)
               );
           }

            axios
                .post(
                    '/wp-admin/admin-ajax.php?action=zmp_quickscan_send_form',
                    post,
                )
                .then((): void => {
                    dispatch({type: 'SEND_RESULT', payload: {}});
                });
        }
    }, [data.valid]);

    return (
        <FormContext.Provider value={{data, dispatch}}>
            {props.children}
        </FormContext.Provider>
    );
}
